import { IFText } from 'Components'
import PropTypes from 'prop-types'
import React, { useState } from 'react'
import Styles from './ConnectorOngoingActions.module.css'
import { useTranslation } from 'react-i18next'
import { Colors, Icons } from 'Theme'
import { CreditCard, PhoneIphone } from '@mui/icons-material'
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined'
import InfinityEnums from 'Enums/InfinityEnums'
import ReactTimeAgo from 'react-time-ago'
import IFsvg from 'Components/IFsvg/IFsvg'
import { isPropsMatch } from 'Utils/PropsMatch'
import AnalyticsIcon from '@mui/icons-material/Analytics'
import styled from 'styled-components'
import CheckIcon from '@mui/icons-material/Check'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'

const StyledHyperLink = styled.a`
  font-size: 14;
  vertical-align: middle;
  text-align: center;
  padding-bottom: 2px;
  margin-left: 6px;
  color: ${(props) => props.textColor};
  &:hover {
    color: ${(props) => props.hoverColor} !important;
  }
  &:focus {
    color: ${(props) => props.hoverColor} !important;
  }
`
// The ongoing-session header shows the actual fine session status (from the
// connector's active session: Charging / SuspendedEV / SuspendedEVSE / EVConnected),
// which the coarse connector pill no longer carries. Falls back to a generic
// "Ongoing" when only a coarse status is available.
const getFineStatusLabel = (status, t) => {
  switch (status) {
    case InfinityEnums.ConnectorStatus.CHARGING:
      return t('OngoingSession.Charging')
    case InfinityEnums.ChargingSessionChargingState.SUSPENDEDEV:
      return t('OngoingSession.SuspendedEV')
    case InfinityEnums.ChargingSessionChargingState.SUSPENDEDEVSE:
      return t('OngoingSession.SuspendedEVSE')
    case InfinityEnums.ChargingSessionChargingState.EVCONNECTED:
      return t('OngoingSession.EVConnected')
    default:
      return null
  }
}

const ConnectorOngoingActions = ({
  status,
  chargingTokenUid,
  chargingTokenId,
  powerConsumed,
  startedAt,
  reservation,
  tokenType,
  onIconClick,
}) => {
  const { t } = useTranslation()
  const [tokenHover, setTokenHover] = useState()
  const [isHuidCopied, setIsHuidCopied] = useState(false)
  const animateUid = () => {
    setIsHuidCopied(!isHuidCopied)
    setTimeout(() => setIsHuidCopied(false), 2000)
  }
  return (
    <div
      className={Styles.Container}
      style={{
        borderColor:
          status === InfinityEnums.ConnectorStatus.CHARGING
            ? Colors.ChargingConnector
            : Colors.PreparingConnector,
      }}
    >
      <div
        className={Styles.OngoingTextContainer}
        style={{
          backgroundColor:
            status === InfinityEnums.ConnectorStatus.CHARGING
              ? Colors.ChargingConnector
              : Colors.PreparingConnector,
        }}
      >
        <IFText className={Styles.OngoingText} style={{ color: Colors.white }}>
          {reservation
            ? t('OngoingSession.Reserved')
            : getFineStatusLabel(status, t) || t('OngoingSession.Ongoing')}
        </IFText>
      </div>
      <div
        className={Styles.DetailsContainer}
        style={{
          backgroundColor:
            status === InfinityEnums.ConnectorStatus.CHARGING
              ? Colors.ChargingConnector + Colors.HexTransparent.T5
              : Colors.PreparingConnector + Colors.HexTransparent.T5,
        }}
      >
        <div className={Styles.TokenContainer}>
          <>
            {tokenType === InfinityEnums.ChargingTokenType.VIRTUAL ? (
              <PhoneIphone sx={{ fontSize: 20 }} />
            ) : (
              <CreditCard
                sx={{ fontSize: 20 }}
                className={Styles.CreditCardIcon}
              />
            )}
            <StyledHyperLink
              hoverColor={Colors.primary}
              textColor={Colors.text}
              target="_blank"
              href={`/cards/${chargingTokenId}`}
            >
              <IFText className={Styles.Text}>#{chargingTokenUid}</IFText>
            </StyledHyperLink>
            <div
              className={Styles.UidCopyContainer}
              onMouseEnter={() => setTokenHover(true)}
              onMouseLeave={() => setTokenHover(false)}
              onClick={(e) => e.stopPropagation()}
            >
              {tokenHover ? (
                <div
                  className={Styles.iconContainer}
                  onClick={() => {
                    navigator.clipboard.writeText(chargingTokenUid)
                  }}
                >
                  <CheckIcon
                    className={isHuidCopied ? Styles.visible : Styles.hide}
                    style={{
                      color: Colors.primary,
                    }}
                  />
                  <ContentCopyIcon
                    onClick={animateUid}
                    style={{ color: Colors.ClipboardCopy }}
                    className={!isHuidCopied ? Styles.visible : Styles.hide}
                  />
                </div>
              ) : null}
            </div>
          </>
        </div>
        <div className={Styles.EnergyWrapper}>
          {!reservation && (
            <div className={Styles.EnergyContainer}>
              <div className={Styles.FlashContainer}>{Icons.flash}</div>
              <IFText className={Styles.Text}>
                {isNaN(powerConsumed)
                  ? `0 kWh`
                  : `${Number(powerConsumed / 1000).toFixed(3)} kWh`}
              </IFText>
            </div>
          )}
        </div>
        <div className={Styles.TimeContainer}>
          {reservation ? (
            <div style={{ alignSelf: 'center' }}>
              <IFsvg.Timelapse
                height={24}
                width={24}
                fill={Colors.black}
                style={{ alignSelf: 'center' }}
              />
            </div>
          ) : (
            <TimerOutlinedIcon className={Styles.TimerOutlinedIcon} />
          )}

          <IFText className={Styles.Text}>
            {reservation ? (
              <ReactTimeAgo
                future
                date={new Date(reservation.expiresAt)}
                locale="en-US"
                timeStyle="round"
              />
            ) : (
              <ReactTimeAgo
                date={new Date(startedAt)}
                locale="en-US"
                timeStyle="mini"
              />
            )}
          </IFText>
        </div>
        {!reservation && (
          <div className={Styles.ChargingEventsContainer}>
            <AnalyticsIcon
              className={Styles.AnalyticsIcon}
              onClick={onIconClick}
            />
          </div>
        )}
      </div>
    </div>
  )
}

ConnectorOngoingActions.propTypes = {
  status: PropTypes.string,
  chargingTokenUid: PropTypes.string,
  powerConsumed: PropTypes.number,
  startedAt: PropTypes.string,
}

function shouldSkipRender(prevProps, nextProps) {
  return isPropsMatch(prevProps, nextProps, [
    'status',
    'chargingTokenUid',
    'powerConsumed',
    'startedAt',
    'tokenType',
    'reservation',
  ])
}
export default React.memo(ConnectorOngoingActions, shouldSkipRender)
